/**
 * 메뉴 관리 — 어드민 사이드바 메뉴 항목별 노출 on/off
 *
 * 코드 정의(`components/nav-items`)의 `hidden`이 기본값이고,
 * 이 화면에서 저장한 override(app_settings `admin_menu_visibility`)가 그것을 덮어쓴다.
 * off는 사이드바 노출만 막고 라우트는 그대로 열려 있다.
 */

import { useCallback, useMemo, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Alert, Badge, Button, ConfirmDialog, Text, Toggle } from '@/ui'
import { useToast } from '@/components/ToastContainer'
import { useApiMutation } from '@/hooks/useApiMutation'
import { NAV_ITEMS, isSubGroup, type NavItem } from '@/components/nav-items'
import {
  MENU_VISIBILITY_SETTING_KEY,
  flattenItems,
  isItemVisible,
  manageableNavItems,
  setItemsVisibility,
  type MenuVisibilityOverrides,
} from '@/components/nav-visibility'
import { useMenuVisibility } from '@/lib/menu-visibility'
import styles from './MenuSettings.module.css'

const SETTING_LABEL = '어드민 메뉴 노출 설정'
const SETTING_DESCRIPTION = '어드민 사이드바 메뉴 항목별 노출 여부. 키는 라우트 경로.'

export function MenuSettings() {
  const { addToast } = useToast()
  const { overrides, isLoading, applyOverrides, refetch } = useMenuVisibility()
  const { trigger, isLoading: saving } = useApiMutation('/api/admin/settings', 'PATCH')
  const [resetOpen, setResetOpen] = useState(false)

  const groups = useMemo(() => manageableNavItems(NAV_ITEMS, { isDev: import.meta.env.DEV }), [])

  /** override 맵 전체를 1회 PATCH — 항목 단위든 그룹 일괄이든 요청 1건. */
  const save = useCallback(async (next: MenuVisibilityOverrides, message: string) => {
    const result = await trigger({
      key: MENU_VISIBILITY_SETTING_KEY,
      value: next,
      category: 'SYSTEM',
      label: SETTING_LABEL,
      description: SETTING_DESCRIPTION,
    })
    if (result) {
      applyOverrides(next)   // 사이드바 즉시 반영
      addToast({ type: 'success', message })
      refetch()
    } else {
      addToast({ type: 'error', message: '설정 저장에 실패했습니다.' })
    }
  }, [trigger, applyOverrides, addToast, refetch])

  const toggleItem = useCallback((item: NavItem, next: boolean) => {
    save(
      setItemsVisibility(overrides, [item], next),
      `${item.label} 메뉴를 ${next ? '노출' : '숨김'} 처리했습니다.`,
    )
  }, [overrides, save])

  const toggleMany = useCallback((label: string, items: NavItem[], next: boolean) => {
    save(
      setItemsVisibility(overrides, items, next),
      `${label} 메뉴를 일괄 ${next ? '노출' : '숨김'} 처리했습니다.`,
    )
  }, [overrides, save])

  const handleReset = useCallback(async () => {
    setResetOpen(false)
    await save({}, '메뉴 노출 설정을 기본값으로 되돌렸습니다.')
  }, [save])

  const changedCount = Object.keys(overrides).length

  if (isLoading) return null

  /** 항목 한 줄 렌더 */
  const renderItem = (item: NavItem, indented: boolean) => {
    const visible = isItemVisible(item, overrides)
    const changed = !item.locked && overrides[item.to] !== undefined
    return (
      <div key={item.to} className={indented ? `${styles.row} ${styles.rowIndented}` : styles.row}>
        <span className={styles.rowIcon}>{item.icon}</span>
        <span className={styles.rowLabel}>{item.label}</span>
        <span className={styles.rowPath}>{item.to}</span>
        <span className={styles.rowBadges}>
          {item.locked && <Badge color="gray">필수</Badge>}
          {item.devOnly && <Badge color="yellow">dev 전용</Badge>}
          {changed && <Badge color="blue">기본값과 다름</Badge>}
        </span>
        <Toggle
          checked={visible}
          onChange={(next) => toggleItem(item, next)}
          disabled={saving || item.locked}
          size="sm"
          aria-label={`${item.label} 메뉴 노출`}
        />
      </div>
    )
  }

  /**
   * 그룹/서브그룹 헤더의 일괄 토글 — 하나라도 노출 중이면 on.
   * locked 항목은 끌 수 없으니 판정에서도 제외한다 (안 그러면 토글이 켜진 채 멈춘 것처럼 보인다).
   */
  const renderBulkToggle = (label: string, items: NavItem[]) => {
    const togglable = items.filter((item) => !item.locked)
    return (
      <Toggle
        checked={togglable.some((item) => isItemVisible(item, overrides))}
        onChange={(next) => toggleMany(label, togglable, next)}
        disabled={saving || togglable.length === 0}
        size="sm"
        aria-label={`${label} 전체 노출`}
      />
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text variant="body-sm" color="secondary">
          사이드바에 표시할 메뉴를 고른다. {changedCount > 0 ? `기본값과 다른 항목 ${changedCount}개.` : '전부 기본값.'}
        </Text>
        <Button variant="secondary" size="sm" onClick={() => setResetOpen(true)} disabled={saving || changedCount === 0}>
          <RotateCcw size={14} /> 기본값 복원
        </Button>
      </div>

      <Alert variant="info">
        메뉴를 숨겨도 페이지 URL 직접 접근은 가능하다 (기존 북마크·딥링크 유지). 설정은 전역이라 모든 관리자에게 적용된다.
      </Alert>

      {groups.map((group) => {
        const groupItems = flattenItems(group.items)
        return (
          <div key={group.group} className={styles.group}>
            <div className={styles.groupHeader}>
              <Text variant="heading-sm">{group.group}</Text>
              <Text variant="caption" color="tertiary">
                {groupItems.filter((item) => isItemVisible(item, overrides)).length} / {groupItems.length} 노출
              </Text>
              <span className={styles.spacer} />
              {renderBulkToggle(group.group, groupItems)}
            </div>

            {group.items.map((item) => {
              if (!isSubGroup(item)) return renderItem(item, false)
              return (
                <div key={item.subgroup} className={styles.subgroup}>
                  <div className={styles.subgroupHeader}>
                    <Text variant="label" color="secondary">{item.subgroup}</Text>
                    <span className={styles.spacer} />
                    {renderBulkToggle(item.subgroup, item.items)}
                  </div>
                  {item.items.map((sub) => renderItem(sub, true))}
                </div>
              )
            })}
          </div>
        )
      })}

      <ConfirmDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={handleReset}
        title="기본값 복원"
        message="메뉴 노출 설정을 모두 지우고 코드 기본값으로 되돌린다. 계속할까요?"
        confirmLabel="복원"
        loading={saving}
      />
    </div>
  )
}
